import type { InterfaceDensity } from './interfacePreferences'
import type { ListViewState } from '../context/AdminListStateContext'

/** Presentation metadata only. Callers supply columns already authorized for this context. */
export interface TableColumn<Id extends string = string> {
  id: Id
  /** Translation key; domain callers may supply an already localized label. */
  label: string
  required?: boolean
  defaultVisible?: boolean
  minWidth?: string
}
export interface TableColumnsState { hiddenColumnIds: readonly string[] }
export interface TablePreferenceScope {
  userId: string
  /** Null denotes an explicitly verified tenantless workspace. Unresolved scope is null itself. */
  tenantId: string | null
  workspaceId: string
  organizationId: string | null
  tableId: string
}
/** Operations remain in domain adapters; mixed client/server sources are explicit. */
export interface TableOperationModes {
  pagination: 'client' | 'server'
  sorting: 'client' | 'server' | 'none'
  filtering: 'client' | 'server' | 'none'
  search: 'client' | 'server' | 'none'
}
export interface TableState extends TableColumnsState {
  query: ListViewState
  density: InterfaceDensity
}
/** Browser-private view schema. Never serialize records, selection, authority or arbitrary queries.
 * Runtime codecs project only explicitly safe fields and revalidate current metadata.
 */
export interface PersonalTableViewV1 {
  version: 1
  id: string
  name: string
  tableId: string
  columns: TableColumnsState & { order?: readonly string[] }
  sort: { columnId: string; direction: 'asc' | 'desc' } | null
  filters: Readonly<Record<string, string | number | boolean>>
  search?: { text: string }
  density: InterfaceDensity
  pagination?: { pageSize: number }
}

export function defaultHiddenColumns(columns: readonly TableColumn[]): string[] {
  return columns.filter(column => !column.required && column.defaultVisible === false).map(column => column.id)
}
/** Unknown/removed IDs and required columns can never enter the hidden set. */
export function normalizeHiddenColumns(value: readonly unknown[], columns: readonly TableColumn[]): string[] {
  return columns.filter(column => !column.required && value.includes(column.id)).map(column => column.id)
}

export type TableFilterValue = string | number | boolean | undefined
export interface TableFilterDefinition<F extends Record<string, TableFilterValue>> {
  id: Extract<keyof F, string>
  label: string
  scope: 'source' | 'page'
  /** Only explicit, non-sensitive enum criteria may enter browser-local views. */
  persist?: boolean
  defaultValue?: TableFilterValue
  accept: (value: TableFilterValue) => boolean
  /** Domain formatter translates labels, never user-supplied names or identifiers. */
  format?: (value: TableFilterValue, translate: (key: string) => string) => string
}
export interface TableDefinition<F extends Record<string, TableFilterValue>> {
  workspaceId: string
  id: string
  stateKey: string
  densityKey: string
  columns: readonly TableColumn[]
  operations: TableOperationModes
  sorts: readonly { id: string; label: string }[]
  defaultSort: { id: string; direction: 'asc' | 'desc' }
  filters: readonly TableFilterDefinition<F>[]
}
/** Restored controls are presentation, never authority. Locked context filters win. */
export function normalizeTableView<F extends Record<string, TableFilterValue>>(view: ListViewState, definition: TableDefinition<F>, locked: Partial<F> = {}): ListViewState {
  const filters: Record<string, TableFilterValue> = {}
  for (const filter of definition.filters) {
    const value = view.filters[filter.id]
    filters[filter.id] = filter.accept(value) ? value : filter.defaultValue
  }
  Object.assign(filters, locked)
  const next: ListViewState = { page: Number.isInteger(view.page) && view.page > 0 ? view.page : 1,
    pageSize: [10,25,50,100].includes(view.pageSize) ? view.pageSize : 25,
    search: typeof view.search === 'string' ? view.search : '',
    sortBy: definition.sorts.some(sort => sort.id === view.sortBy) ? view.sortBy : definition.defaultSort.id,
    sortDir: view.sortDir === 'desc' || view.sortDir === 'asc' ? view.sortDir : definition.defaultSort.direction,
    filters }
  return JSON.stringify(next) === JSON.stringify(view) ? view : next
}
/** Null/empty/invalid last in either direction; equal values preserve stable Array.sort order. */
export function compareTableValues(a: unknown, b: unknown, kind: 'text' | 'number' | 'date', direction: 'asc' | 'desc', collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })) {
  const value = (raw: unknown): string | number | null => {
    if (raw === null || raw === undefined || String(raw).trim() === '') return null
    if (kind === 'text') return String(raw)
    const n = kind === 'number' ? Number(raw) : raw instanceof Date ? raw.getTime() : Date.parse(String(raw))
    return Number.isFinite(n) ? n : null
  }
  const av = value(a), bv = value(b)
  if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1
  return (kind === 'text' ? collator.compare(String(av), String(bv)) : Number(av)-Number(bv)) * (direction === 'desc' ? -1 : 1)
}

export interface TableSelectionConfig<T> {
  enabled?: boolean
  getId: (record: T) => string
  isEligible: (record: T) => boolean
}
